/**
 * Stores Home - Manage Next Stores
 */
import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@trafiflow/shared-ui'
import { Button } from '@trafiflow/shared-ui'
import { Plus, Edit, Trash2, Store as StoreIcon, ExternalLink } from 'lucide-react'
import apiClient from '../../../shared/lib/api'
import type { NextStore } from '../types'
import { NextStoreModal } from '../components'

export default function StoresHome() {
  const { user } = useAuth()
  const [stores, setStores] = useState<NextStore[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingStore, setEditingStore] = useState<NextStore | null>(null)

  const fetchStores = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get('/next-stores')
      setStores(response.data.stores || [])
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { message?: string } } }
      if (error.response?.status === 404) {
        setStores([])
        setError(null)
      } else {
        console.error('Error fetching next stores:', err)
        setError(error.response?.data?.message || 'Failed to load stores')
        setStores([])
      }
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useEffect(() => {
    fetchStores()
  }, [fetchStores])

  const handleCreate = () => {
    setEditingStore(null)
    setIsModalOpen(true)
  }

  const handleEdit = (store: NextStore) => {
    setEditingStore(store)
    setIsModalOpen(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta tienda?')) {
      return
    }

    try {
      await apiClient.delete(`/next-stores/${id}`)
      await fetchStores()
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } }
      console.error('Error deleting store:', err)
      alert(error.response?.data?.message || 'Failed to delete store')
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingStore(null)
  }

  const handleModalSave = async () => {
    await fetchStores()
    handleModalClose()
  }

  if (loading && stores.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-tf-text-muted">Cargando tiendas...</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-tf-text">Stores</h2>
          <p className="text-sm text-tf-text-muted mt-1">
            Gestiona tus Next Stores
          </p>
        </div>
        <Button
          variant="primary"
          onClick={handleCreate}
          icon={<Plus className="w-4 h-4" />}
        >
          Agregar Store
        </Button>
      </div>

      {error && (
        <div className="bg-tf-feedback-error-bg border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {stores.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-tf-bg-hover rounded-full flex items-center justify-center mx-auto mb-4">
            <StoreIcon className="w-8 h-8 text-tf-text-muted" />
          </div>
          <h3 className="text-lg font-semibold text-tf-text mb-2">No hay tiendas</h3>
          <p className="text-tf-text-muted mb-4">
            Comienza agregando tu primera tienda
          </p>
          <Button
            variant="primary"
            onClick={handleCreate}
            icon={<Plus className="w-4 h-4" />}
          >
            Agregar Store
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-tf-border">
            <thead className="bg-tf-bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                  Store
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                  Entity ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                  Creada
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-tf-text-muted uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-tf-bg-card divide-y divide-tf-border">
              {stores.map((store) => (
                <tr key={store.id} className="hover:bg-tf-bg-muted">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {store.logoUrl ? (
                        <img
                          src={store.logoUrl}
                          alt={store.name}
                          className="w-8 h-8 rounded-full mr-3 object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-tf-bg-hover flex items-center justify-center mr-3">
                          <StoreIcon className="w-4 h-4 text-tf-text-muted" />
                        </div>
                      )}
                      <div>
                        <span className="text-sm font-medium text-tf-text">{store.name}</span>
                        {store.description && (
                          <p className="text-xs text-tf-text-muted mt-1">{store.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={store.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:text-primary-dark flex items-center gap-1"
                    >
                      {store.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-tf-text-muted">
                      {store.agentplatEntityId || '-'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-tf-text-muted">
                    {new Date(store.createdAt).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(store)}
                        className="text-primary hover:text-primary-dark"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(store.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NextStoreModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleModalSave}
        store={editingStore}
      />
    </div>
  )
}


